import React, { createContext, useContext, useEffect, useState } from 'react';
import { isUserAuthenticated, clearAuthData } from '../utils/cookieUtils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication status
  const checkAuthStatus = () => {
    try {
      const authStatus = isUserAuthenticated();
      setIsAuthenticated(authStatus);
      
      // If not authenticated and not on sign-in page, redirect
      if (!authStatus) {
        const currentPath = window.location.pathname;
        if (currentPath !== '/sign-in' && currentPath !== '/') {
          clearAuthData();
          window.location.href = '/sign-in';
        }
      }
    } catch (error) {
      //console.error('Auth check failed:', error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle logout
  const logout = () => {
    clearAuthData();
    setIsAuthenticated(false);
    window.location.href = '/sign-in';
  };

  // Check auth status on mount and set up periodic checks
  useEffect(() => {
    checkAuthStatus();

    // Set up periodic token validation (every 5 minutes)
    const interval = setInterval(() => {
      checkAuthStatus();
    }, 5 * 60 * 1000);

    // Listen for storage changes (for multi-tab sync)
    const handleStorageChange = (e) => {
      if (e.key === 'logout' || e.key === 'token') {
        checkAuthStatus();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const value = {
    isAuthenticated,
    isLoading,
    checkAuthStatus,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
